import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Chip,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import DataTable from '../components/DataTable';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';
import UserManagementDialog from '../components/UserManagementDialog';
import { useOrgCuratedCurrencies } from '../hooks/useOrgCuratedCurrencies';
import { formatCurrencyPickerLabel } from '../utils/iso4217';

interface Account {
  id: string;
  organizationId: string;
  companyId: string;
  name: string;
  description?: string;
  accountNumber?: string;
  billingRate?: number;
  currency: string;
  isActive: boolean;
  settings?: any;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  company?: {
    id: string;
    name: string;
    currency?: string;
  };
  tasks?: {
    id: string;
    name: string;
    taskNumber: string;
    isBillable: boolean;
    billingRate?: number;
  }[];
}

interface AccountFormData {
  organizationId: string;
  companyId: string;
  name: string;
  description: string;
  accountNumber: string;
  billingRate: string;
  currency: string;
  isActive: boolean;
  defaultTasks: string[];
}

export default function Accounts() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const { formatDateOnly } = useDateFormat();
  const [formData, setFormData] = useState<AccountFormData>({
    organizationId: '',
    companyId: '',
    name: '',
    description: '',
    accountNumber: '',
    billingRate: '',
    currency: 'EUR',
    isActive: true,
    defaultTasks: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [userManagementOpen, setUserManagementOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const { curatedCodes } = useOrgCuratedCurrencies(formData.organizationId || undefined);

  useEffect(() => {
    if (curatedCodes.length === 0) return;
    setFormData((prev) => {
      if (curatedCodes.includes(prev.currency)) return prev;
      return { ...prev, currency: curatedCodes[0] };
    });
  }, [curatedCodes]);

  useEffect(() => {
    loadAccounts();
    loadCompanies();
    loadOrganizations();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAccounts();
      setAccounts(response.data || []);
    } catch (err: any) {
      console.error('Error loading accounts:', err);
      setError(err.response?.data?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.getCompanies();
      setCompanies(response.data || []);
    } catch (err: any) {
      console.error('Error loading companies:', err);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await api.getOrganizations();
      setOrganizations(response.data || []);
    } catch (err: any) {
      console.error('Error loading organizations:', err);
    }
  };

  // Filter companies based on selected organization
  const getFilteredCompanies = () => {
    if (!formData.organizationId) {
      return [];
    }
    return companies.filter(company => company.organizationId === formData.organizationId);
  };

  // Handle organization change - reset company selection
  const handleOrganizationChange = (organizationId: string) => {
    setFormData({
      ...formData,
      organizationId,
      companyId: '', // Reset company when organization changes
      defaultTasks: [] // Reset default tasks when organization changes
    });
  };

  const handleCompanyChange = (companyId: string) => {
    const selectedCompany = companies.find((c) => c.id === companyId);
    setFormData({
      ...formData,
      companyId,
      currency: selectedCompany?.currency || formData.currency,
      defaultTasks: selectedCompany?.defaultTasks || []
    });
  };

  const handleOpenDialog = (account?: Account) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        organizationId: account.organizationId,
        companyId: account.companyId,
        name: account.name,
        description: account.description || '',
        accountNumber: account.accountNumber || '',
        billingRate: account.billingRate?.toString() || '',
        currency: account.currency,
        isActive: account.isActive,
        defaultTasks: []
      });
    } else {
      setEditingAccount(null);
      setFormData({
        organizationId: '',
        companyId: '',
        name: '',
        description: '',
        accountNumber: '',
        billingRate: '',
        currency: 'EUR',
        isActive: true,
        defaultTasks: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAccount(null);
    setFormData({
      organizationId: '',
      companyId: '',
      name: '',
      description: '',
      accountNumber: '',
      billingRate: '',
      currency: 'EUR',
      isActive: true,
      defaultTasks: []
    });
  };

  const handleSubmit = async () => {
    try {
      const submitData = {
        ...formData,
        billingRate: formData.billingRate ? parseFloat(formData.billingRate) : null
      };

      if (editingAccount) {
        await api.updateAccount(editingAccount.id, submitData);
      } else {
        await api.createAccount(submitData);
      }
      handleCloseDialog();
      loadAccounts();
    } catch (err: any) {
      console.error('Error saving account:', err);
      setError(err.response?.data?.message || 'Failed to save account');
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await api.deleteAccount(accountId);
        loadAccounts();
      } catch (err: any) {
        console.error('Error deleting account:', err);
        setError(err.response?.data?.message || 'Failed to delete account');
      }
    }
  };

  const handleOpenUserManagement = (account: Account) => {
    setSelectedAccount(account);
    setUserManagementOpen(true);
  };

  const handleCloseUserManagement = () => {
    setUserManagementOpen(false);
    setSelectedAccount(null);
  };

  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (account.description && account.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && account.isActive) ||
                         (filterStatus === 'inactive' && !account.isActive);
    return matchesSearch && matchesStatus;
  });

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    { 
      field: 'company', 
      headerName: 'Company', 
      width: 150,
      valueGetter: (params) => params.row.company?.name || 'N/A'
    },
    { field: 'accountNumber', headerName: 'Account Number', width: 150 },
    { 
      field: 'billingRate', 
      headerName: 'Billing Rate', 
      width: 120,
      valueFormatter: (params) => params.value ? `$${params.value}` : 'N/A'
    },
    { field: 'currency', headerName: 'Currency', width: 100 },
    { 
      field: 'isActive', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Active' : 'Inactive'} 
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      )
    },
    { 
      field: 'createdAt', 
      headerName: 'Created', 
      width: 120,
      valueFormatter: (params) => formatDateOnly(params.value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 210,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<PeopleIcon />}
          label="Manage Users"
          onClick={() => handleOpenUserManagement(params.row)}
        />,
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteAccount(params.row.id)}
        />
      ]
    }
  ];

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Accounts Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search accounts"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter by Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                {filteredAccounts.length} of {accounts.length} accounts
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Accounts Table */}
      <DataTable
        rows={filteredAccounts}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
      />

      {/* Add/Edit Account Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAccount ? 'Edit Account' : 'Add New Account'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={formData.organizationId}
                    label="Organization"
                    onChange={(e) => handleOrganizationChange(e.target.value)}
                    disabled={!!editingAccount}
                  >
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Company</InputLabel>
                  <Select
                    value={formData.companyId}
                    label="Company"
                    onChange={(e) => handleCompanyChange(e.target.value)}
                    disabled={!formData.organizationId}
                  >
                    {getFilteredCompanies().map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Billing Rate"
                  type="number"
                  value={formData.billingRate}
                  onChange={(e) => setFormData({ ...formData, billingRate: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{formData.currency}</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!formData.organizationId || curatedCodes.length === 0}>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={formData.currency}
                    label="Currency"
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  >
                    {curatedCodes.map((code) => (
                      <MenuItem key={code} value={code}>
                        {formatCurrencyPickerLabel(code)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              {!editingAccount && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Account Tasks
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    These tasks will be automatically created for this account. You can modify the list before creating the account.
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.defaultTasks}
                    onChange={(_, newValue) => {
                      setFormData({ ...formData, defaultTasks: newValue });
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          key={index}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Account Tasks"
                        placeholder="Type a task name and press Enter"
                        helperText="Press Enter to add a new task"
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </Box>
          
          {/* Tasks Table - Only show when editing */}
          {editingAccount && editingAccount.tasks && editingAccount.tasks.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Account Tasks
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Task Number</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Billable</TableCell>
                      <TableCell>Billing Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editingAccount.tasks.map((task) => (
                      <TableRow key={task.id}>
                        <TableCell>{task.taskNumber}</TableCell>
                        <TableCell>{task.name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={task.isBillable ? 'Yes' : 'No'} 
                            color={task.isBillable ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {task.billingRate ? `$${task.billingRate}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAccount ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Management Dialog */}
      {selectedAccount && (
        <UserManagementDialog
          open={userManagementOpen}
          onClose={handleCloseUserManagement}
          title={selectedAccount.name}
          entityId={selectedAccount.id}
          entityType="account"
          organizationId={selectedAccount.organizationId}
        />
      )}
    </Box>
  );
}
